import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL, titleize } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
    AlertCircle, CheckCircle2, Upload, FileSpreadsheet, Download, Copy,
    RefreshCw, XCircle, Info
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const STATUS_OPTIONS = ["draft", "pending", "published", "unclaimed"];

export default function AdminImport() {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [defaultStatus, setDefaultStatus] = useState("pending");
    const [rowSelection, setRowSelection] = useState({}); // row_number -> boolean
    const [importResult, setImportResult] = useState(null);

    const upload = async () => {
        if (!file) { toast.error("Choose a CSV file first"); return; }
        setLoading(true); setImportResult(null); setPreview(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/import/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
            // By default: select every valid row (including duplicates - admin decides individually)
            const sel = {};
            for (const r of data.rows) sel[r.row_number] = r.valid;
            setRowSelection(sel);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Preview failed");
        } finally { setLoading(false); }
    };

    const executeImport = async () => {
        if (!preview) return;
        const rows = preview.rows.filter((r) => rowSelection[r.row_number] && r.valid);
        if (rows.length === 0) { toast.error("Select at least one valid row"); return; }
        setImporting(true);
        try {
            const { data } = await api.post("/admin/import/execute", { rows, default_status: defaultStatus });
            setImportResult(data);
            toast.success(`Imported ${data.inserted} venues`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed");
        } finally { setImporting(false); }
    };

    const downloadTemplate = () => {
        window.open(`${BACKEND_URL}/api/admin/import/template`, "_blank");
    };

    const selectedCount = preview ? preview.rows.filter((r) => rowSelection[r.row_number] && r.valid).length : 0;

    return (
        <div data-testid="admin-import-page">
            <div className="flex justify-between items-start flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="font-display text-3xl font-semibold">CSV Importer</h1>
                    <p className="text-sm text-muted-foreground mt-1">Bulk-seed venues from a CSV file. Preview and validate before importing.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={downloadTemplate} data-testid="download-template">
                        <Download className="h-4 w-4 mr-1" /> Download template
                    </Button>
                </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
                <div className="grid md:grid-cols-[1fr_auto] gap-4 items-end">
                    <div>
                        <label className="text-sm font-medium">CSV file</label>
                        <div className="mt-2 flex items-center gap-3">
                            <label className="flex-1 flex items-center gap-3 rounded-lg border border-dashed border-border px-4 py-3 cursor-pointer hover:bg-muted/40 transition-colors">
                                <FileSpreadsheet className="h-5 w-5 text-secondary" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{file?.name || "Choose CSV file"}</p>
                                    <p className="text-xs text-muted-foreground">{file ? `${(file.size / 1024).toFixed(1)} KB` : "UTF-8, header row required"}</p>
                                </div>
                                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                                    onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setImportResult(null); }}
                                    data-testid="csv-file-input" />
                            </label>
                        </div>
                    </div>
                    <Button onClick={upload} disabled={!file || loading} className="rounded-full h-11 px-6 bg-primary text-primary-foreground" data-testid="preview-btn">
                        {loading ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                        Preview import
                    </Button>
                </div>

                <div className="mt-4 rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground flex gap-2 items-start">
                    <Info className="h-4 w-4 shrink-0 mt-0.5" />
                    <div>
                        <strong>Required columns:</strong> name, country, city, venue_type, short_description. Multi-value columns (image_urls, event_types, amenities) accept comma or pipe (|) separators. Latitude/longitude optional (we'll geocode when needed).
                    </div>
                </div>
            </div>

            {preview && (
                <div className="mt-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        <StatCard label="Total rows" value={preview.stats.total} />
                        <StatCard label="Valid" value={preview.stats.valid} accent="green" />
                        <StatCard label="Invalid" value={preview.stats.invalid} accent="red" />
                        <StatCard label="Duplicates" value={preview.stats.duplicates} accent="amber" />
                    </div>

                    <div className="rounded-2xl border border-border bg-card overflow-hidden">
                        <div className="p-4 md:p-5 flex flex-wrap items-center justify-between gap-3 border-b border-border">
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-medium">{selectedCount} selected for import</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Import as:</span>
                                    <Select value={defaultStatus} onValueChange={setDefaultStatus}>
                                        <SelectTrigger className="w-[140px] h-9" data-testid="default-status"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <Button onClick={executeImport} disabled={importing || selectedCount === 0} className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="execute-import-btn">
                                {importing ? "Importing…" : `Import ${selectedCount} row${selectedCount === 1 ? "" : "s"}`}
                            </Button>
                        </div>

                        <div className="max-h-[560px] overflow-auto">
                            <Table>
                                <TableHeader className="sticky top-0 bg-card z-10">
                                    <TableRow>
                                        <TableHead className="w-10"></TableHead>
                                        <TableHead>Row</TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>City</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Notes</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {preview.rows.map((r) => (
                                        <TableRow key={r.row_number} className={r.valid ? "" : "bg-red-50/40"} data-testid={`preview-row-${r.row_number}`}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={!!rowSelection[r.row_number]}
                                                    disabled={!r.valid}
                                                    onCheckedChange={(c) => setRowSelection((s) => ({ ...s, [r.row_number]: !!c }))}
                                                    data-testid={`row-check-${r.row_number}`}
                                                />
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">{r.row_number}</TableCell>
                                            <TableCell className="max-w-[220px] truncate font-medium">{r.parsed.name || <span className="text-red-500">(missing)</span>}</TableCell>
                                            <TableCell>{r.parsed.city}{r.parsed.country ? `, ${r.parsed.country}` : ""}</TableCell>
                                            <TableCell className="text-xs">{titleize(r.parsed.venue_type || "")}</TableCell>
                                            <TableCell>
                                                {r.valid
                                                    ? (r.duplicates.length > 0
                                                        ? <Badge className="bg-amber-100 text-amber-800 gap-1"><AlertCircle className="h-3 w-3" />Possible duplicate</Badge>
                                                        : <Badge className="bg-green-100 text-green-800 gap-1"><CheckCircle2 className="h-3 w-3" />Valid</Badge>)
                                                    : <Badge className="bg-red-100 text-red-800 gap-1"><XCircle className="h-3 w-3" />Invalid</Badge>
                                                }
                                            </TableCell>
                                            <TableCell className="text-xs max-w-[300px]">
                                                {r.errors.length > 0 && (
                                                    <div className="text-red-600">
                                                        {r.errors.map((e, i) => <div key={i}>• {e}</div>)}
                                                    </div>
                                                )}
                                                {r.duplicates.length > 0 && (
                                                    <div className="text-amber-700 mt-1">
                                                        Similar to: {r.duplicates.map((d) => d.name).join(", ")}
                                                    </div>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>
            )}

            {importResult && (
                <div className="mt-6 rounded-2xl border border-border bg-accent/30 p-6" data-testid="import-result">
                    <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                        <div>
                            <h3 className="font-display text-xl font-semibold">Import complete</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                <b>{importResult.inserted}</b> venue{importResult.inserted === 1 ? "" : "s"} imported successfully.
                                {importResult.skipped > 0 && <> {importResult.skipped} skipped.</>}
                            </p>
                            {importResult.errors?.length > 0 && (
                                <div className="mt-3 text-xs text-red-700">
                                    {importResult.errors.slice(0, 5).map((e, i) => (
                                        <div key={i}>Row {e.row_number}: {e.error}</div>
                                    ))}
                                </div>
                            )}
                            <div className="mt-4 flex gap-2">
                                <Button asChild variant="outline" size="sm"><Link to="/admin/venues">View all venues</Link></Button>
                                <Button size="sm" variant="ghost" onClick={() => { setFile(null); setPreview(null); setImportResult(null); if (fileRef.current) fileRef.current.value = ""; }}>Import another</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, accent }) {
    const accentMap = {
        green: "text-green-700",
        red: "text-red-700",
        amber: "text-amber-700",
    };
    return (
        <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
            <p className={`font-display text-2xl font-semibold mt-1 ${accentMap[accent] || ""}`}>{value}</p>
        </div>
    );
}
